import { type PropsWithChildren, Fragment, useContext, useState } from "react";
import { Fab } from "@mui/material";
import {
  FiArchive,
  FiClipboard,
  FiFrown,
  FiLogIn,
  FiLogOut,
  FiPrinter,
  FiTrello,
} from "react-icons/fi";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";

import { Booking, BookingCancellationReason, BookingStatus, CollectionDeliveryStatus, LaneAllocationSystem, VehicleStatus } from "../../../logic/api";
import styles from "./styles";
import AnimateFrom from "../../../components/animations/AnimateFrom";
import { useLabelPrinter } from "../../../components/labels";
import { useLabelTemplates } from "../../../components/labels/labelTemplates";
import { SessionContext } from "../../../utility/useSession";
import { isAdmin } from "../../../utility/utils";
import { BookingDetailsPrint } from "../BookingDetailPrint/BookingDetailsPrint";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { useTranslation } from "react-i18next";
import { BookingFindKey } from "../BookFindKey/BookingFindKey";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { BiTargetLock } from "react-icons/bi";

export const BookingFabLabel = ({ text, children }: PropsWithChildren<{ text: string }>) => (
  <>
    <div style={styles.fabLabel}>{text}</div>
    {children}
  </>
);

export class BookingFabController {
  printCheckInBarcodes = (booking: Booking) => {
    console.log("No handler assigned for printCheckInBarcodes", booking);
  };
}

type BookingFabProps = {
  allowUpdatingBooking: boolean;
  booking: Booking;
  controller: BookingFabController;
  onRequestLabelPrint: (booking: Booking) => Promise<void>;
  onAllocateBooking: () => void;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onCancel: () => void;
  onUnArchive: () => void;
  onShowComplaints: () => void;
  onShowInspections: () => void;
  onShowDataRetention: () => void;
  onShowAllowKeyRelease: () => void;
  onShowFindKey: () => void;
  onRestore: () => void;
  onUndoCheckOut?: (booking: Booking) => void;
  onUndoCheckIn?: (booking: Booking) => void;
  onUpdateCollectionDeliveryStatus?: (bookingUuid: string, status: CollectionDeliveryStatus) => void;
};

const BookingFabV2 = (props: BookingFabProps) => {
  const [expanded, setExpanded] = useState(false);
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();

  // Feature flags
  const deprecateArchiveReleased = useReleaseManagement(ReleaseFlag.releaseDeprecateArchiveStatus);
  const bookingUndoCheckout = useReleaseManagement(ReleaseFlag.releaseBookingUndoCheckout);
  const isDataManagementReleased = useReleaseManagement(ReleaseFlag.releaseDataManagement);
  const isUndoCheckInReleased = useReleaseManagement(ReleaseFlag.releaseUndoCheckIn);
  const isFindKeyReleased = useReleaseManagement(ReleaseFlag.releaseFindKey);
  const isDualParkReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
  const isDualParkFabAllocationReleased = useReleaseManagement(ReleaseFlag.releaseDualParkAllocateFab);
  const isPrintBookingDetailsReleased = useReleaseManagement(ReleaseFlag.releasePrintBookingDetailsSetting);
  const isCollectionDeliveryMeetGreetReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreet);
  const isWrongOptionInFabMenuFixReleased = useReleaseManagement(ReleaseFlag.releaseWrongOptionsInFabMenu);
  const isRestoreBookingUncancelReleased = useReleaseManagement(ReleaseFlag.releaseBookingUncancel);
  const isBookingStatusStuckInPendingFixReleased = useReleaseManagement(ReleaseFlag.releaseBookingStatusStuckInPending);

  // Label printing
  const { labelPrinter } = useLabelPrinter();
  const {
    generateParkAndRidePrintJob,
    generateMeetAndGreetPrintJob,
    generateVehiclePrintJob,
  } = useLabelTemplates();

  // Booking state
  const isCheckedIn = props.booking.vehicle.statusId === VehicleStatus.CheckedIn;
  const isCheckedOut = props.booking.vehicle.statusId === VehicleStatus.CheckedOut;
  const isPreCheckin = [VehicleStatus.NotCheckedIn, VehicleStatus.PreCheckIn].includes(
    props.booking.vehicle.statusId
  );
  const isCancelled = props.booking.statusId === BookingStatus.Cancelled;
  const collectionDeliveryStatus = props.booking.vehicle.collectionDeliveryStatus;
  const hasCollectionProduct = props.booking.collection === true;
  const hasDeliveryProduct = props.booking.delivery === true;
  const canUpdateCollectionDeliveryStatus = isCollectionDeliveryMeetGreetReleased && hasPermission("bookingManagement.booking.updateStatus");
  const showActionsInvalidForCancelledBooking = !isRestoreBookingUncancelReleased || !isCancelled;
  const canRestoreBooking =
    isRestoreBookingUncancelReleased &&
    isCancelled &&
    (props.booking.cancellationReason === BookingCancellationReason.StaffCancellation ||
      props.booking.cancellationReason === BookingCancellationReason.NonArrival) &&
    hasPermission("bookingManagement.booking.uncancel");

  // Site prefs
  const allowPrintBeforeCheckin = session.current.site?.preferences.allow_printing_labels_before_checkin;
  const showComplaints = session.current.site?.preferences.complaints_enabled;
  const showInspections = session.current.site?.preferences.inspections_enabled;
  const booking_checkout_admin_enabled =
    isAdmin(session) && session.current.site?.preferences.booking_checkout_admin_enabled;
  const isDualPark = session.current.site?.preferences.lane_allocation_system === LaneAllocationSystem.DualPark;
  const showPrintBookingDetails = !isPrintBookingDetailsReleased || session.current.site?.preferences.booking_print_details_enabled;

  const createButton = (
    text: string,
    icon: JSX.Element,
    id: string,
    color?: string,
    onClick?: () => void
  ) => (
    <Fab
      key={id}
      style={color ? { ...styles.fab, backgroundColor: color } : styles.fab}
      onClick={() => {
        setExpanded(false);
        onClick?.();
      }}
      data-testid={id}
    >
      <BookingFabLabel text={text}>{icon}</BookingFabLabel>
    </Fab>
  );

  const buildButtons = () => {
    const buttons: JSX.Element[] = [];

    // 1) Data Retention (admin-only)
    if (isAdmin(session) && isDataManagementReleased && hasPermission("bookingManagement.data.retain")) {
      buttons.push(
        createButton(
          t("booking.dataRetention.menuOption"),
          <FiTrello size={26} />,
          "Fab-Retention",
          "rgb(100, 100, 100)",
          props.onShowDataRetention
        )
      );
    }

    if (isPreCheckin) {
      // 2) Cancel/Archive Booking
      if (!isCancelled && props.allowUpdatingBooking && hasPermission("bookingManagement.booking.cancel")) {
        buttons.push(
          createButton(
            deprecateArchiveReleased ? t("booking.labels.cancelBooking") : t("booking.labels.archiveBooking"),
            <FiArchive size={26} />,
            deprecateArchiveReleased ? "Fab-Cancel-Booking" : "Fab-Archive-Booking",
            "rgb(203, 36, 49)",
            props.onCancel
          )
        );
      }

      // 2.5) Undo Arrival Confirmed
      if (showActionsInvalidForCancelledBooking && canUpdateCollectionDeliveryStatus && hasCollectionProduct) {
        if (collectionDeliveryStatus === CollectionDeliveryStatus.CollectionRequested) {
          buttons.push(
            createButton(
              t("booking.labels.undoCollectionRequested"),
              <UndoIcon style={{ fontSize: 26 }} />,
              "Fab-Undo-Arrival-Confirmed",
              "rgb(246, 166, 86)",
              () => props.onUpdateCollectionDeliveryStatus?.(props.booking.uuid, CollectionDeliveryStatus.Pending)
            )
          );
        }
      }

      //Find Keys
      if (showActionsInvalidForCancelledBooking && isFindKeyReleased) buttons.push(<BookingFindKey key="find-keys" onClick={() => {
        setExpanded(false);
        props.onShowFindKey();
      }} />);


      // 3) Print Booking Details
      if (showPrintBookingDetails) {
        buttons.push(<BookingDetailsPrint key="print-details" booking={props.booking} onClick={() => setExpanded(false)} />);
      }
      // 4) Complaints
      if (showComplaints) {
        buttons.push(
          createButton(
            t("complaints.title"),
            <FiFrown size={26} />,
            "Fab-Complaints",
            "rgb(13, 89, 87)",
            props.onShowComplaints
          )
        );
      }
      // 5) Inspections
      if (showActionsInvalidForCancelledBooking && showInspections) {
        buttons.push(
          createButton(
            t("booking.labels.inspections"),
            <FiClipboard size={26} />,
            "Fab-Inspections",
            "rgb(58, 183, 169)",
            props.onShowInspections
          )
        );
      }
      // 6) Print Label
      if (showActionsInvalidForCancelledBooking && allowPrintBeforeCheckin) {
        buttons.push(
          createButton(
            t("booking.labels.printLabel"),
            <FiPrinter size={26} />,
            "Fab-Print-Label",
            "rgb(13, 89, 87)",
            () => props.onRequestLabelPrint(props.booking)
          )
        );
      }
      // 7) Check In
      const hideCheckInForCollection = isCollectionDeliveryMeetGreetReleased && hasCollectionProduct && collectionDeliveryStatus !== CollectionDeliveryStatus.CollectionRequested;
      if (!isCancelled && props.allowUpdatingBooking && hasPermission("bookingManagement.booking.updateStatus") && !hideCheckInForCollection) {
        buttons.push(
          createButton(
            t("booking.labels.checkin"),
            <FiLogIn size={26} />,
            "Fab-Check-In",
            "rgb(142, 189, 76)",
            props.onCheckIn
          )
        );
      }
      // 8) Arrival Confirmed
      if (showActionsInvalidForCancelledBooking && canUpdateCollectionDeliveryStatus && hasCollectionProduct) {
        if (collectionDeliveryStatus === CollectionDeliveryStatus.Pending) {
          buttons.push(
            createButton(
              t("booking.labels.collectionRequested"),
              <LocalPhoneOutlinedIcon style={{ fontSize: 26 }} />,
              "Fab-Arrival-Confirmed",
              "rgb(142, 189, 76)",
              () => props.onUpdateCollectionDeliveryStatus?.(props.booking.uuid, CollectionDeliveryStatus.CollectionRequested)
            )
          );
        }
      }
    } else if (isCheckedIn) {
      // 2) Undo Check-in (hidden when return confirmed (DeliveryRequested))
      const hideUndoCheckInForDelivery = isCollectionDeliveryMeetGreetReleased && hasDeliveryProduct && collectionDeliveryStatus === CollectionDeliveryStatus.DeliveryRequested;
      // Undo Check-in (hidden when collection is in progress; when collection is only requested, undoing check-in is the escape hatch and also resets the collection status back to Pending)
      const blockedCollectionStatusesForUndoCheckIn = isBookingStatusStuckInPendingFixReleased
        ? [CollectionDeliveryStatus.CollectionInProgress]
        : [CollectionDeliveryStatus.CollectionRequested, CollectionDeliveryStatus.CollectionInProgress];
      const hideUndoCheckInForCollection = isWrongOptionInFabMenuFixReleased && isCollectionDeliveryMeetGreetReleased && hasCollectionProduct && collectionDeliveryStatus && blockedCollectionStatusesForUndoCheckIn.includes(collectionDeliveryStatus);
      if (isUndoCheckInReleased && hasPermission("bookingManagement.booking.revert") && !hideUndoCheckInForDelivery && !hideUndoCheckInForCollection) {
        buttons.push(
          createButton(
            t("booking.labels.undoCheckin"),
            <UndoIcon style={{ fontSize: 26 }} />,
            "Fab-Undo-Checkin",
            "rgb(246, 166, 86)",
            () => props.onUndoCheckIn?.(props.booking)
          )
        );
      }

      // 2.5) Undo Return Confirmed
      if (showActionsInvalidForCancelledBooking && canUpdateCollectionDeliveryStatus && hasDeliveryProduct) {
        if (collectionDeliveryStatus === CollectionDeliveryStatus.DeliveryRequested) {
          buttons.push(
            createButton(
              t("booking.labels.undoDeliveryRequested"),
              <UndoIcon style={{ fontSize: 26 }} />,
              "Fab-Undo-Return-Confirmed",
              "rgb(246, 166, 86)",
              () => props.onUpdateCollectionDeliveryStatus?.(props.booking.uuid, CollectionDeliveryStatus.CollectionComplete)
            )
          );
        }
      }

      // 2.6) Undo Arrival Confirmed (checked in before the collection status caught up)
      if (showActionsInvalidForCancelledBooking && isWrongOptionInFabMenuFixReleased && canUpdateCollectionDeliveryStatus && hasCollectionProduct && !isBookingStatusStuckInPendingFixReleased) {
        if (collectionDeliveryStatus === CollectionDeliveryStatus.CollectionRequested) {
          buttons.push(
            createButton(
              t("booking.labels.undoCollectionRequested"),
              <UndoIcon style={{ fontSize: 26 }} />,
              "Fab-Undo-Arrival-Confirmed",
              "rgb(246, 166, 86)",
              () => props.onUpdateCollectionDeliveryStatus?.(props.booking.uuid, CollectionDeliveryStatus.Pending)
            )
          );
        }
      }

      //Find Keys
      if (showActionsInvalidForCancelledBooking && isFindKeyReleased) buttons.push(<BookingFindKey key="find-keys" onClick={() => {
        setExpanded(false);
        props.onShowFindKey();
      }} />);

      // 3) Print Booking Details
      if (showPrintBookingDetails) {
        buttons.push(<BookingDetailsPrint key="print-details" booking={props.booking} onClick={() => setExpanded(false)} />);
      }
      // 4) Complaints
      if (showComplaints) {
        buttons.push(
          createButton(
            t("complaints.title"),
            <FiFrown size={26} />,
            "Fab-Complaints",
            "rgb(58, 183, 169)",
            props.onShowComplaints
          )
        );
      }
      // 5) Inspections
      if (showActionsInvalidForCancelledBooking && showInspections) {
        buttons.push(
          createButton(
            t("booking.labels.inspections"),
            <FiClipboard size={26} />,
            "Fab-Inspections",
            "rgb(58, 183, 169)",
            props.onShowInspections
          )
        );
      }
      // 6) Print Label
      if (showActionsInvalidForCancelledBooking) buttons.push(
        createButton(
          t("booking.labels.printLabel"),
          <FiPrinter size={26} />,
          "Fab-Print-Label",
          "rgb(13, 89, 87)",
          () => props.onRequestLabelPrint(props.booking)
        )
      );
      // 6.5) (dual park only) Allocate Booking
      if (isDualParkReleased && isDualParkFabAllocationReleased && isDualPark) {
        buttons.push(
          createButton(
            t("booking.labels.allocate"),
            <BiTargetLock size={26} />,
            "Fab-Allocate-Booking",
            "rgb(58, 183, 169)",
            props.onAllocateBooking
          )
        );
      }
      // 7) Check Out (hidden when delivery product exists and pick-up not yet requested)
      const hideCheckOutForDelivery = isCollectionDeliveryMeetGreetReleased && hasDeliveryProduct && collectionDeliveryStatus && [CollectionDeliveryStatus.CollectionComplete, CollectionDeliveryStatus.NoCollection].includes(collectionDeliveryStatus);
      // Check Out (hidden when there's a collection product on the booking and collection is requested or in progress)
      const hideCheckOutForCollection = isWrongOptionInFabMenuFixReleased && isCollectionDeliveryMeetGreetReleased && hasCollectionProduct && collectionDeliveryStatus && [CollectionDeliveryStatus.CollectionRequested, CollectionDeliveryStatus.CollectionInProgress].includes(collectionDeliveryStatus);
      if ((booking_checkout_admin_enabled || props.allowUpdatingBooking) && hasPermission("bookingManagement.booking.updateStatus") && !hideCheckOutForDelivery && !hideCheckOutForCollection) {
        buttons.push(
          createButton(
            t("booking.labels.checkout"),
            <FiLogOut size={26} />,
            "Fab-Check-Out",
            "rgb(142, 189, 76)",
            props.onCheckOut
          )
        );
      }
      // 8) Return Confirmed
      if (showActionsInvalidForCancelledBooking && canUpdateCollectionDeliveryStatus && hasDeliveryProduct) {
        //(CollectionComplete after collection flow, NoCollection for delivery-only)
        //(also offered while checked in but the collection status hasn't caught up to CollectionComplete yet)
        const collectionStatusCaughtUp = collectionDeliveryStatus != null && [CollectionDeliveryStatus.CollectionComplete, CollectionDeliveryStatus.NoCollection].includes(collectionDeliveryStatus);
        const checkedInBeforeCollectionComplete = isWrongOptionInFabMenuFixReleased && hasCollectionProduct && collectionDeliveryStatus != null && [CollectionDeliveryStatus.CollectionRequested, CollectionDeliveryStatus.CollectionInProgress].includes(collectionDeliveryStatus);
        if (collectionStatusCaughtUp || checkedInBeforeCollectionComplete) {
          buttons.push(
            createButton(
              t("booking.labels.deliveryRequested"),
              <LocalPhoneOutlinedIcon style={{ fontSize: 26 }} />,
              "Fab-Return-Confirmed",
              "rgb(142, 189, 76)",
              () => props.onUpdateCollectionDeliveryStatus?.(props.booking.uuid, CollectionDeliveryStatus.DeliveryRequested)
            )
          );
        }
      }
    } else if (isCheckedOut) {
      // 2) Undo Check-out
      if (bookingUndoCheckout && hasPermission("bookingManagement.booking.revert")) {
        buttons.push(
          createButton(
            t("booking.labels.undoCheckout"),
            <UndoIcon style={{ fontSize: 26 }} />,
            "Fab-Undo-Checkout",
            "rgb(246, 166, 86)",
            () => props.onUndoCheckOut?.(props.booking)
          )
        );
      }

      //Find Keys
      if (showActionsInvalidForCancelledBooking && isFindKeyReleased) buttons.push(<BookingFindKey key="find-keys" onClick={() => {
        setExpanded(false);
        props.onShowFindKey();
      }} />);


      // 3) Print Booking Details
      if (showPrintBookingDetails) {
        buttons.push(<BookingDetailsPrint key="print-details" booking={props.booking} onClick={() => setExpanded(false)} />);
      }
      // 4) Complaints
      if (showComplaints) {
        buttons.push(
          createButton(
            t("complaints.title"),
            <FiFrown size={26} />,
            "Fab-Complaints",
            "rgb(58, 183, 169)",
            props.onShowComplaints
          )
        );
      }
      // 5) Inspections
      if (showActionsInvalidForCancelledBooking && showInspections) {
        buttons.push(
          createButton(
            t("booking.labels.inspections"),
            <FiClipboard size={26} />,
            "Fab-Inspections",
            "rgb(58, 183, 169)",
            props.onShowInspections
          )
        );
      }
      // 6) Print Label
      if (showActionsInvalidForCancelledBooking) buttons.push(
        createButton(
          t("booking.labels.printLabel"),
          <FiPrinter size={26} />,
          "Fab-Print-Label",
          "rgb(13, 89, 87)",
          () => props.onRequestLabelPrint(props.booking)
        )
      );
    }

    if (canRestoreBooking) {
      buttons.push(
        createButton(
          t("booking.labels.restoreBooking"),
          <UndoIcon style={{ fontSize: 26 }} />,
          "Fab-Restore-Booking",
          "rgb(246, 166, 86)",
          props.onRestore
        )
      );
    }

    return buttons;
  };


  // Assign printing logic
  props.controller.printCheckInBarcodes = async (booking) => {
    const job =
      booking.selfPark === true
        ? generateParkAndRidePrintJob
        : booking.collection === true || booking.delivery === true
          ? generateMeetAndGreetPrintJob
          : generateVehiclePrintJob;
    await new Promise<void>((resolve) => labelPrinter.print(job(booking), resolve));
  };

  const renderButtons = buildButtons();
  if (renderButtons.length === 0) return <Fragment />;

  return (
    <Fragment>
      {expanded && (
        <AnimateFrom delay={0} duration={1} style={{ opacity: 0 }}>
          <div style={styles.fabBg} />
        </AnimateFrom>
      )}
      <div style={styles.fabPosition}>
        {expanded &&
          renderButtons.map((btn, i) => (
            <AnimateFrom key={i} delay={0.1 * (renderButtons.length - i)} style={{ opacity: 0, zoom: 2 }}>
              {btn}
            </AnimateFrom>
          ))}
        <Fab
          style={expanded ? { ...styles.fab, ...styles.fabClose, ...styles.fabRoot } : { ...styles.fab, ...styles.fabRoot }}
          onClick={() => setExpanded(!expanded)}
          data-testid="Fab-Booking-Menu"
        >
          {expanded ? <CloseIcon style={{ fontSize: 26 }} /> : <MenuIcon style={{ fontSize: 26 }} />}
        </Fab>
      </div>
    </Fragment>
  );
};

export default BookingFabV2;
